import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Auth } from "aws-amplify";
import AWS from "aws-sdk";
import axios from "axios";
import { DateTime } from "luxon";
import { apiAuth } from "../../Backend/api";
import { propertyLinkPath } from "../../Helper/propertyLink";
import { guestResumePath } from "../../Helper/guestStay";

export const useLobbyCode = (room, booking) => {
  const [lobbyCode, setLobbyCode] = useState(null);
  const [lobbyCodeLoading, setLobbyCodeLoading] = useState(true); // start as loading
  const [lobbyCodeError, setLobbyCodeError] = useState(false);

  useEffect(() => {
    const fetchLobbyCode = async () => {
      setLobbyCodeLoading(true); // always show loading first
      setLobbyCodeError(false);
      if (!room?.ciboCode || !booking?.pmsBookingID) {
        setLobbyCode(null);
        setLobbyCodeLoading(false);
        setLobbyCodeError(false);
        return;
      }
      // Check localStorage after loading spinner
      const storageKey = `lobbyCode_${booking.pmsBookingID}`;
      let code = null;
      let fetchFailed = false;
      try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        const response = await axios.get(
          `https://api.linkbase.ca/cibo/lobby-code/${booking.pmsBookingID}`,
          {
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
            },
          }
        );
        if (response.data?.success && response.data.data?.lobbyCode) {
          code = response.data.data.lobbyCode;
          localStorage.setItem(storageKey, code);
        }
      } catch (error) {
        fetchFailed = true;
      }
      // If no code from API, try localStorage
      if (!code) {
        code = localStorage.getItem(storageKey);
      }
      setLobbyCode(code);
      setLobbyCodeLoading(false);
      setLobbyCodeError(fetchFailed && !code);
    };
    fetchLobbyCode();
  }, [room?.ciboCode, booking?.pmsBookingID]);

  return { lobbyCode, lobbyCodeLoading, lobbyCodeError };
};

// Configure AWS for S3 image fetching
AWS.config.update({
  region: "us-west-2",
  credentials: new AWS.Credentials(
    process.env.REACT_APP_PUBLIC_KEY,
    process.env.REACT_APP_SECRET_KEY
  ),
});

export const useMainV2Logic = () => {
  const location = useLocation();
  const { room, booking, manager: locationManager, accessTimes } = location.state || {};
  const [imageUrl, setImageUrl] = useState(null);
  const navigate = useNavigate();

  // Get manager data from Redux as fallback if not in location state
  const reduxManager = useSelector((state) => ({
    email: state.user.email,
    password: state.user.password,
    stripeId: state.user.stripeId,
  }));

  // Use location manager if available, otherwise use Redux manager
  const manager = locationManager || reduxManager;

  // Guides state
  const [guides, setGuides] = useState([]);
  const [guideCatalogList, setGuideCatalogList] = useState([]);
  const [guidesLoad, setGuidesLoad] = useState(true);
  const [filteredGuides, setFilteredGuides] = useState([]);

  // Weather state
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  // Weather cache
  const getWeatherCache = (roomId) => {
    const cacheKey = `weather_${roomId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      const cacheAge = now - timestamp;
      const cacheExpiry = 30 * 60 * 1000; // 30 minutes
      
      if (cacheAge < cacheExpiry) {
        return data;
      } else {
        // Cache expired, remove it
        localStorage.removeItem(cacheKey);
      }
    }
    return null;
  };

  const setWeatherCache = (roomId, weatherData) => {
    const cacheKey = `weather_${roomId}`;
    const cacheData = {
      data: weatherData,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  };

  useEffect(() => {
    if (!room || !booking) {
      const propertyPath = propertyLinkPath();
      if (propertyPath) {
        navigate(propertyPath, { replace: true });
        return;
      }
      // The tab was reloaded (often the phone discarding it in the
      // background) and the route state is gone: reload the booking through
      // /go, which continues straight back here.
      navigate(guestResumePath(), { replace: true });
      return;
    }

    // Defensive gate. /go writes `securityStepsComplete: true` onto the
    // booking before navigating here; anything else (false, missing —
    // e.g. user back-buttoned in with stale state) means lock and send
    // them through /onboarding. Server-truth, lock-by-default.
    if (booking.securityStepsComplete !== true) {
      // Read manager/accessTimes from the location.state snapshot
      // directly so we don't have to reach for the closure value
      // (which is the unstable redux fallback). They're snapshots tied
      // to this navigation; a new navigation re-runs the hook anyway.
      const navState = location.state || {};
      navigate("/onboarding", {
        replace: true,
        state: {
          room,
          booking,
          manager: navState.manager,
          accessTimes: navState.accessTimes,
        },
      });
    }
  }, [room, booking, navigate, location.state]);

  // Fetch weather data
  useEffect(() => {
    if (room?.id) {
      fetchWeather(room.id);
    }
  }, [room?.id]);

  const fetchWeather = async (roomId) => {
    try {
      // Check cache first
      const cachedWeather = getWeatherCache(roomId);
      if (cachedWeather) {
        setWeather(cachedWeather);
        return; // Use cached data, no need to fetch
      }

      setWeatherLoading(true);
      
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      const response = await axios.get(`https://api.linkbase.ca/rooms/${roomId}/weather`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
        }
      });

      if (response.data.success) {
        setWeather(response.data.data.weather);
        setWeatherCache(roomId, response.data.data.weather); // Cache the weather data
      }
    } catch (error) {
      console.error("Error fetching weather:", error);
    } finally {
      setWeatherLoading(false);
    }
  };

  // Fetch guides data
  useEffect(() => {
    if (room?.id) {
      fetchGuides(room.id);
    }
  }, [room?.id]);

  const fetchGuides = async (roomId) => {
    try {
      setGuidesLoad(true);
      
      // Mock guides data for now - replace with actual API call
      const mockGuides = [
        {
          id: "1",
          title: "Property Guide",
          images: ["https://via.placeholder.com/120x95"],
          guideCatalogID: "resort-map"
        },
        {
          id: "2", 
          title: "Amenities Guide",
          images: ["https://via.placeholder.com/120x95"],
          guideCatalogID: "resort-amenities"
        }
      ];

      const mockGuideCatalog = [
        { id: "all", name: "All" },
        { id: "resort-map", name: "Resort Map" },
        { id: "resort-amenities", name: "Resort Amenities" }
      ];

      setGuides(mockGuides);
      setGuideCatalogList(mockGuideCatalog);
      setFilteredGuides(mockGuides);
    } catch (error) {
      console.error("Error fetching guides:", error);
    } finally {
      setGuidesLoad(false);
    }
  };

  const handleDownload = async (key) => {
    const s3 = new AWS.S3();

    if (!key) return null;

    const params = {
      Bucket: process.env.REACT_APP_BUCKET,
      Key: `public/${key}`,
    };

    return new Promise((resolve, reject) => {
      s3.getSignedUrl("getObject", params, (err, url) => {
        if (err) {
          reject(err);
        } else {
          resolve(url);
        }
      });
    });
  };

  useEffect(() => {
    const fetchImage = async () => {
      if (room?.images?.[0]) {
        const imageData = room.images[0];
        
        // Check if it's already a URL (starts with http/https)
        if (imageData.startsWith('http://') || imageData.startsWith('https://')) {
          setImageUrl(imageData);
          return;
        }
        
        // If it's an S3 key, fetch the signed URL
        try {
          const url = await handleDownload(imageData);
          setImageUrl(url);
        } catch (error) {
          console.error("Error fetching image:", error);
          // Fallback to direct S3 URL
          const fallbackUrl = `https://linkbase-admin.s3.us-west-2.amazonaws.com/public/${imageData}`;
          setImageUrl(fallbackUrl);
        }
      }
    };

    fetchImage();
  }, [room?.images]);

  const getAddressText = () => {
    if (!room?.address) return "";
    
    try {
      const parsedAddress = JSON.parse(room.address);
      return parsedAddress.formatted_address
        .split(",")
        .slice(0, -2)
        .join(",");
    } catch (error) {
      console.error("Error parsing address:", error);
      return "";
    }
  };

  // Check if door should be disabled based on booking dates
  const isDoorDisabled = () => {
    if (!booking?.checkInDate || !booking?.checkOutDate) {
      return false;
    }

    try {
      // Use room timezone or default to LA timezone
      const timezone = room?.owner?.timezone || "America/Los_Angeles";
      const now = DateTime.now().setZone(timezone);
      const checkInDate = DateTime.fromISO(booking.checkInDate).setZone(timezone);
      const checkOutDate = DateTime.fromISO(booking.checkOutDate).setZone(timezone);

      // Door is disabled if current date is before check-in or after check-out
      const isDisabled = now < checkInDate || now > checkOutDate;
      return isDisabled;
    } catch (error) {
      console.error("Error checking door availability:", error);
      return false;
    }
  };

  // Door Access logic
  const [doorState, setDoorState] = useState(true); // true = locked
  const [doorLoading, setDoorLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [doorError, setDoorError] = useState(null);

  // Menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Cibo modal state
  const [isCiboOpen, setIsCiboOpen] = useState(false);

  // Menu handlers
  const handleOpenMenu = () => {
    setIsMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  // Cibo handlers
  const handleOpenCibo = () => {
    setIsCiboOpen(true);
  };

  const handleCloseCibo = () => {
    setIsCiboOpen(false);
  };

  // Process ciboLink with reservationID
  const getProcessedCiboLink = () => {
    if (!room?.ciboLink || !booking?.reservationID) return room?.ciboLink;
    
    try {
      const ciboData = JSON.parse(room.ciboLink);
      return `${ciboData.url}${booking.reservationID}`;
    } catch (error) {
      console.error("Error parsing ciboLink:", error);
      return room.ciboLink;
    }
  };

  // Check if lobby should be disabled based on booking dates
  const isLobbyDisabled = () => {
    return isDoorDisabled();
  };

  // Call actual API for lock/unlock
  const handleDoor = async () => {
    if (!room?.roomDeviceId || !booking?.id) {
      console.error("Missing roomDeviceId or booking ID");
      return;
    }

    if (isDoorDisabled()) {
      console.log("Door access is disabled - outside booking dates");
      return;
    }

    setDoorLoading(true);
    setDoorError(null);
    setProgress(0);
    const EstimatedApiResponseTime = 2000;
    const interval = 100;
    let elapsed = 0;
    const maxProgressBeforeCompletion = 90;

    const timer = setInterval(() => {
      elapsed += interval;
      const progressValue = Math.min((elapsed / EstimatedApiResponseTime) * 100, maxProgressBeforeCompletion);
      setProgress(progressValue);
    }, interval);

    try {
      await apiAuth(`/devices/${room.roomDeviceId}/toggle`, {
        bookingId: booking.id,
        timezone: room?.owner?.timezone || "America/Los_Angeles",
      });

      setDoorState((prev) => !prev);
      setProgress(100);
    } catch (error) {
      const status = error?.response?.status;
      const responseData = error?.response?.data;

      if (status === 403 && responseData?.details) {
        setDoorError({
          kind: "window",
          message: responseData.error || "Door access is not available right now.",
          details: responseData.details,
        });
      } else {
        setDoorError({
          kind: "generic",
          message:
            responseData?.error || "Couldn't reach the lock. Please try again.",
          details: null,
        });
      }

      console.error("Error toggling door:", error);
    } finally {
      clearInterval(timer);
      setDoorLoading(false);
    }
  };

  const clearDoorError = () => setDoorError(null);

  return {
    room,
    booking,
    accessTimes,
    imageUrl,
    getAddressText,
    doorState,
    doorLoading,
    progress,
    handleDoor,
    isDoorDisabled,
    doorError,
    clearDoorError,
    // Guides data
    guides,
    guideCatalogList,
    guidesLoad,
    filteredGuides,
    // Weather data
    weather,
    weatherLoading,
    manager, // Add manager to the return object
    isMenuOpen,
    handleOpenMenu,
    handleCloseMenu,
    // Cibo data
    isCiboOpen,
    handleOpenCibo,
    handleCloseCibo,
    getProcessedCiboLink,
    isLobbyDisabled,
  };
}; 